import React, { useState, useEffect, useRef } from 'react';
import './VideosSection.css'; 
import LanternLabs2 from '../assets/LanternLabs2.png';
import LanternLabs3 from '../assets/LanternLabs3.png';
const VideosSection = () => {
    const [videos, setVideos] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedVideoId, setSelectedVideoId] = useState(null);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        // Fetch CMS data directly from public folder
        const fetchVideos = async () => {
            try {
                const response = await fetch('/Videos_CMS.json');
                if (!response.ok) throw new Error('Failed to load JSON');
                const data = await response.json();
                setVideos(data);
            } catch (error) {
                console.error('Error loading videos:', error);
            }
        };
        fetchVideos();
    }, []);

    const extractYoutubeId = (url) => {
        if (!url) return null;
        const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;
        const match = url.match(youtubeRegex);
        return match ? match[1] : null;
    };

    const handleVideoClick = (e, videoLink) => {
        e.preventDefault();
        const id = extractYoutubeId(videoLink);
        if (id) {
            setSelectedVideoId(id);
        } else {
            window.open(videoLink, '_blank');
        }
    };

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            const maxScroll = scrollWidth - clientWidth;
            if (maxScroll <= 0) return;

            const scrollPercentage = scrollLeft / maxScroll;
            const index = Math.min(Math.round(scrollPercentage * (videos.length - 1)), videos.length - 1);
            setActiveIndex(index);
        }
    };

    const scrollSide = (direction) => {
        if (scrollContainerRef.current) {
            const width = scrollContainerRef.current.clientWidth;
            const scrollAmount = direction === 'left' ? -width : width;
            scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    const getThumbnailSrc = (thumbnail) => {
        if (!thumbnail) return '/lantern_header.svg';

        // Check for YouTube video ID to extract high-res thumbnail
        const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;
        const match = thumbnail.match(youtubeRegex);

        if (match && match[1]) {
            return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
        }

        // Check if it's already an absolute URL (e.g., direct image link)
        if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
            return thumbnail;
        }

        // Otherwise it's a local path in the public folder
        return thumbnail;
    };

    const dotCount = Math.min(videos.length, 3);
    const activeDotIndex = videos.length <= 3
        ? activeIndex
        : Math.min(Math.floor((activeIndex / videos.length) * 3), 2);

    return (
        <section className="videos-section-container">
            {/* Background Text Decoration */}
            <div className="videos-bg-text-container">
                <div className="videos-bg-text-wrapper">
                    <h1 className="videos-bg-text lantern">LANTERN</h1>
                </div>
                <div className="videos-bg-text-wrapper">
                    <h1 className="videos-bg-text labs">LABS</h1>
                </div>
            </div>

            {/* Scattered Decorative Images */}
            <img src={LanternLabs2} alt="" className="videos-decor-img vdecor-1" />
            <img src={LanternLabs3} alt="" className="videos-decor-img vdecor-2" />
            <img src={LanternLabs2} alt="" className="videos-decor-img vdecor-3" />
            <img src={LanternLabs3} alt="" className="videos-decor-img vdecor-4" />
            <img src={LanternLabs2} alt="" className="videos-decor-img vdecor-5" />
            <img src={LanternLabs3} alt="" className="videos-decor-img vdecor-6" />
            <img src={LanternLabs2} alt="" className="videos-decor-img vdecor-7" />
            <img src={LanternLabs3} alt="" className="videos-decor-img vdecor-8" />
            <img src={LanternLabs2} alt="" className="videos-decor-img vdecor-9" />

            <h2 className="videos-main-title">Videos</h2>

            <div
                className="videos-list-container"
                ref={scrollContainerRef}
                onScroll={handleScroll}
            >
                {videos.map((video) => (
                    <div className="video-card" key={video.id}>
                        <div
                            className="video-thumbnail-container"
                            onClick={(e) => handleVideoClick(e, video.link)}
                        >
                            <img
                                src={getThumbnailSrc(video.thumbnail)}
                                alt="Video thumbnail"
                                className="video-thumbnail"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/lantern_header.svg';
                                }}
                            />
                            <div className="video-play-overlay">
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z" stroke="white" strokeWidth="1" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        <div className="video-info-container">
                            <div className="video-info-header">
                                <p className="video-date">{video.date}</p>
                            </div>
                            <div className="video-info-body">
                                <h3 className="video-title">{video.description}</h3>
                            </div>
                            <div className="video-info-footer">
                                <button
                                    onClick={(e) => handleVideoClick(e, video.link)}
                                    className="video-watch-btn"
                                >
                                    Watch now
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination Controls - Positioned Absolute at Bottom */}
            <div className="videos-pagination">
                <button className="video-scroll-btn btn-left" onClick={() => scrollSide('left')} aria-label="Previous Video">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <div className="video-dots">
                    {[...Array(dotCount)].map((_, i) => (
                        <div
                            key={i}
                            className={`video-dot ${i === activeDotIndex ? 'active' : ''}`}
                        ></div>
                    ))}
                </div>
                <button className="video-scroll-btn btn-right" onClick={() => scrollSide('right')} aria-label="Next Video">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

            {/* Video Modal Popup */}
            {selectedVideoId && (
                <div className="video-modal-overlay" onClick={() => setSelectedVideoId(null)}>
                    <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="video-modal-close" onClick={() => setSelectedVideoId(null)}>
                            &times;
                        </button>
                        <div className="video-iframe-container">
                            <iframe
                                src={`https://www.youtube.com/embed/${selectedVideoId}?autoplay=1&modestbranding=1&rel=0`}
                                title="YouTube video player"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default VideosSection;